/**
 * users.js
 * 
 * API for handling getting user endpoints
 */

import axiosInstance from "./axios-instance";

/**
 * Attempt to login a user with username and password
 */
const loginUser = function (payload) {
    return axiosInstance.post("/users/login", payload).then(res => res);
};

/**
 * Attempt to login a staff member with SSO
 */
const loginStaffWithSSO = function (payload) {
    return axiosInstance.post("/app/users/login/sso", payload).then(res => res);
}

/**
 * Retrieve and use the seeded accounts exposed by a local server.
 * These endpoints are not registered in deployed environments.
 */
const getLocalLoginAccounts = function () {
    return axiosInstance.get("/app/users/login/local/accounts").then(res => res);
}

const loginStaffLocally = function (email) {
    return axiosInstance.post("/app/users/login/local", { email }).then(res => res);
}

/**
 * Attempt to signup a user with username and password.
 */
const signUp = function (payload) {
    return axiosInstance.post("/users/register", payload).then(res => res);
};

/**
 * @desc Retrieve information for given staff account
 * @param {*} id 
 * @returns 
 */
const getAppStaffAccount = function (id) {
    return axiosInstance.get(`/app/users/${id}`).then(res => res);
}

/**
* GET request to return a list of Staff users provided in Purapura DB
* @returns List of Staff users
*/
const getAppStaffList = function () {
    return axiosInstance.get("/app/users").then(res => res);
}

/**
 * @desc Retrieve the staff the current user manages ("their team").
 * @returns List of managed staff
 */
const getMyTeam = function () {
    return axiosInstance.get("/app/users/team").then(res => res);
}

/**
 * @desc The permission catalogue plus the caller's own effective flags.
 * @returns { catalogue, own }
 */
const getPermissionCatalogue = function () {
    return axiosInstance.get("/app/permissions").then(res => res);
}

/**
 * @desc Retrieve a staff member's permission detail (role default / override / effective).
 * @param {*} id app_staff.id
 */
const getStaffPermissions = function (id) {
    return axiosInstance.get(`/app/users/${id}/permissions`).then(res => res);
}

/**
 * @desc Set permission override(s) for a staff member.
 * @param {*} id app_staff.id
 * @param {*} payload { key, allowed } or { permissions: [{ key, allowed }] }; allowed=null clears the override
 */
const updateStaffPermissions = function (id, payload) {
    return axiosInstance.put(`/app/users/${id}/permissions`, payload).then(res => res);
}

/**
 * @desc Set whether a staff account is active.
 * @param {*} id app_staff.id
 * @param {*} isActive the new activeness value
 */
const updateStaffActiveness = function (id, isActive) {
    return axiosInstance.put(`/app/users/${id}/active`, { isActive }).then(res => res);
}

/**
 * @desc Managers (team leaders / admins) whose roster the caller may configure.
 * @returns List of managers with member_count
 */
const getConfigurableManagers = function () {
    return axiosInstance.get("/app/managers").then(res => res);
}

/**
 * @desc A manager's roster: candidate members flagged assigned/unassigned.
 * @param {*} id app_staff.id of the manager
 */
const getManagerRoster = function (id) {
    return axiosInstance.get(`/app/users/${id}/roster`).then(res => res);
}

/**
 * @desc Add or remove a member from a manager's roster.
 * @param {*} id app_staff.id of the manager
 * @param {*} payload { memberStaffId, assigned }
 */
const updateManagerRoster = function (id, payload) {
    return axiosInstance.put(`/app/users/${id}/roster`, payload).then(res => res);
}

const resetPassword = function (payload) {
    return axiosInstance.post("/users/resetPassword", payload).then(res => res);
};

export default {
    loginUser,
    loginStaffWithSSO,
    getLocalLoginAccounts,
    loginStaffLocally,
    signUp,
    getAppStaffAccount,
    getAppStaffList,
    getMyTeam,
    getPermissionCatalogue,
    getStaffPermissions,
    updateStaffPermissions,
    updateStaffActiveness,
    getConfigurableManagers,
    getManagerRoster,
    updateManagerRoster,
    resetPassword,
};
